import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { Input, Multiselect } from '../components';
import { QUERY_KEYS, TOAST_DURATION } from '../lib/constants';
import { User } from '../types';
import { NewProjectForm, NewProjectSchema } from '../lib/schemas';
import { useClientsQuery, useCreateProjectMutation } from '../lib/queries';
import { PATHS } from '../lib/paths';
import { clientsPaginationAtom } from '../atoms/clientsPaginationAtom';

export default function NewProject() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [clientsPagination] = useAtom(clientsPaginationAtom);
  const { register, watch, handleSubmit, setValue, formState, reset } = useForm<NewProjectForm>({
    mode: 'all',
    resolver: zodResolver(NewProjectSchema),
  });

  const clients = watch('clients', []) as User[];
  const clientsQuery = useClientsQuery(clientsPagination);
  const projectMutation = useCreateProjectMutation();
  const client = useQueryClient();

  const onSubmit = async (data: NewProjectForm) => {
    projectMutation.mutate(
      { ...data, users: [...data.clients.map((u) => u.id)] },
      {
        onSuccess: (result) => {
          client.refetchQueries({ queryKey: [QUERY_KEYS.PROJECTS] });
          toast.success(t('project.created', { name: result.project.name }), {
            position: 'top-center',
            duration: TOAST_DURATION,
          });
          navigate(PATHS.homePath());
        },
        onError: (error) => {
          const err = error as Error;
          navigate(PATHS.errorPath(err.message));
        },
      },
    );
  };

  useEffect(
    () => {
      setValue('clients', []);
      return () => reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <main className="grid gap-4 w-700 mx-auto">
      <h1 className="text-hero font-bold">{t('project.title')}</h1>
      <form className="grid gap-2 mt-10" onSubmit={handleSubmit(onSubmit)}>
        <Input
          label="project.name"
          type="text"
          {...register('name')}
          error={formState.errors.name?.message?.toString()}
          required
        />
        <Input
          label="project.key"
          type="text"
          {...register('key')}
          error={formState.errors.name?.message?.toString()}
          required
        />
        {clientsQuery.data && (
          <Multiselect
            label="project.clients"
            options={clientsQuery.data?.users}
            show={(r) => r.name}
            value={clients || []}
            onChange={(v) => setValue('clients', v, { shouldValidate: true })}
            error={formState.errors.name?.message?.toString()}
          />
        )}

        <div className="flex justify-end items-center gap-2 mt-4">
          <button className="primary" onClick={() => navigate(PATHS.homePath())} type="button">
            {t('common.cancel')}
          </button>
          <button className="primary-fill" type="submit" disabled={!formState.isValid}>
            {t('project.createProject')}
          </button>
        </div>
      </form>
    </main>
  );
}
